import { useTranslation } from 'react-i18next';
import { useSwalNotification } from '../../../../hooks/useSwalNotification';
import {
  useAddCommentsStatsMutation,
  useAddNewStatsStatusMutation,
  useDeleteStatsStatusMutation,
  useEditStatsStatusMutation,
} from '../../../../reduxToolkit/reducers/usersSlice';

export const useLeaderBoardActions = () => {
  const { t } = useTranslation();
  const [editStatsStatus] = useEditStatsStatusMutation();
  const [deleteStatsStatus] = useDeleteStatsStatusMutation();
  const [commentHandler] = useAddCommentsStatsMutation();
  const [addNewStatsStatus] = useAddNewStatsStatusMutation();
  const { showSwalNotification } = useSwalNotification();
  const submitComment = async (payload: {
    statsId: number;
    selectedStatus: { id: number; statusName: string } | null;
    newFiles: File[];
    leaderId: number | null;
    comment: string;
    onSuccess?: () => void;
    onError?: (error: any) => void;
  }) => {
    if (!payload.leaderId) return;

    const fd = new FormData();
    fd.append('statsId', String(payload.statsId));
    fd.append('comment', payload.comment);
    fd.append('statusId', payload.selectedStatus ? String(payload.selectedStatus.id) : '');
    fd.append('statusName', payload.selectedStatus ? payload.selectedStatus.statusName : '');
    payload.newFiles.forEach((file) => fd.append('screenshots', file));
    try {
      await commentHandler(fd).unwrap();
      await new Promise((r) => setTimeout(r, 500));
      showSwalNotification({
        title: t('leads.notifications.successTitle'),
        text: t('leads.notifications.successText'),
        icon: 'success',
      });
    } catch (error) {
      console.log('error', error);
      await new Promise((r) => setTimeout(r, 500));
      showSwalNotification({
        title: t('leads.notifications.errorCommentTitle'),
        text: t('leads.notifications.errorUpdateText'),
        icon: 'error',
      });
    }
  };

  const saveNewStatus = async (payload: { name: string; colorCode: string }) => {
    try {
      const res = await addNewStatsStatus({
        statusName: payload.name,
        colorCode: payload.colorCode,
      }).unwrap();
      await new Promise((r) => setTimeout(r, 500));
      showSwalNotification({
        title: t('leads.notifications.successTitle'),
        text: t('leads.notifications.successText'),
        icon: 'success',
      });
      return res;
    } catch (error) {
      console.log('error', error);
      await new Promise((r) => setTimeout(r, 500));
      showSwalNotification({
        title: t('leads.notifications.errorTitle') || 'Error',
        text: t('leads.notifications.errorText') || 'Could not add status',
        icon: 'error',
      });
    }
  };

  const deleteStatus = async (payload: { statusId: number }) => {
    try {
      await deleteStatsStatus({ statusId: payload.statusId }).unwrap();
      await new Promise((r) => setTimeout(r, 500));
      showSwalNotification({
        title: t('global.statusMutationsText.successDeleteTitle'),
        text: t('global.statusMutationsText.successDeleteText'),
        icon: 'success',
      });
    } catch (err) {
      console.error('failed to delete status', err);
      await new Promise((r) => setTimeout(r, 500));
      showSwalNotification({
        title: t('global.statusMutationsText.errorTitleDelete'),
        text: t('global.statusMutationsText.errorTextDelete'),
        icon: 'error',
      });
    }
  };

  const editCurrentStatus = async (payload: {
    statusId: number;
    name: string;
    colorCode: string;
    onSuccessCb: () => void;
    onErrorCb: () => void;
  }) => {
    try {
      const res = await editStatsStatus({
        statusId: payload.statusId,
        statusName: payload.name,
        colorCode: payload.colorCode,
      }).unwrap();
      await new Promise((r) => setTimeout(r, 500));
      showSwalNotification({
        title: t('leads.notifications.successTitle'),
        text: t('leads.notifications.successText'),
        icon: 'success',
        confirmCallback: () => payload.onSuccessCb(),
      });
      return res;
    } catch (error) {
      console.log('error', error);
      await new Promise((r) => setTimeout(r, 500));
      showSwalNotification({
        title: t('leads.notifications.errorTitle') || 'Error',
        text: t('leads.notifications.errorText') || 'Could not edit status',
        icon: 'error',
        confirmCallback: () => payload.onErrorCb(),
      });
    }
  };
  return { submitComment, saveNewStatus, deleteStatus, editCurrentStatus };
};
